import React from "react";
import styled from "styled-components";

import Header from "../../commons/Header";
import Footer from "../../commons/Footer";
import ProvideLandingSearch from "../../providers/ProvideLandingSearch";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import {
  outerContainerCss,
  narrowScreenMediaQuery,
  brand_orange_deep,
} from "../../constants/styles";
import {
  WebinarGridCard,
  WebinarGridSkeleton,
} from "../../homes/sections/WebinarCarousel";
import UnlockPanel from "../UnlockPanel";
import useWebinarsIndex from "./useWebinarsIndex";
import FilterBar from "./FilterBar";
import Pager from "./Pager";

// `/memberships` requires login, so an anonymous visitor is sent to the login
// page with a return path rather than bounced through an auth wall.
const CATALOG_PATH = encodeURIComponent("/webinars");
const LOGIN_URL = `/login?redirect_after_login=${CATALOG_PATH}`;
const SIGNUP_URL = `/signup?redirect_after_login=${CATALOG_PATH}`;

const Page = styled.div`
  background: #ffffff;
  min-height: 100vh;
  color: #1f2937;
  font-family: "Montserrat", sans-serif;

  &[data-theme="dark"] {
    background: #0f172a;
    color: #f3f4f6;
  }
`;

const Main = styled.main`
  ${outerContainerCss}
  padding-top: 48px;
  padding-bottom: 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media ${narrowScreenMediaQuery} {
    padding-top: 32px;
  }
`;

const PageHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  font-size: 34px;
  font-weight: 800;
  color: #1f2937;
  margin: 0;
  letter-spacing: -0.01em;

  @media ${narrowScreenMediaQuery} {
    font-size: 26px;
  }

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 14px;
  max-width: 640px;
  margin: 0;
  line-height: 1.6;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

const ResultMeta = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

// Subgrid host: each card spans 7 implicit rows and inherits them via
// `grid-template-rows: subgrid`, so card sections align across each row.
// Column and row gaps are set separately — the card overrides its own inner
// row-gap, and this row-gap only spaces one card-row from the next.
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  column-gap: 20px;
  row-gap: 20px;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    column-gap: 16px;
    row-gap: 16px;
  }
`;

const EmptyState = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 16px;

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.7);
    border-color: rgba(255, 255, 255, 0.14);
  }
`;

const InlineReset = styled.button`
  border: none;
  background: transparent;
  color: ${brand_orange_deep};
  font-weight: 700;
  cursor: pointer;
  text-decoration: underline;
  font-size: 14px;
`;

// WebinarsController#index passes `theme` from the dark_mode_v1 toggle. The
// listing itself is fetched client-side from /api/v1/webinars (via
// useWebinarsIndex), mirroring how the homepage carousels self-fetch.
export default function WebinarsIndexApp({
  theme = "light",
  themeOptions = [],
  typeNameOptions = [],
}) {
  const resolved = theme === "dark" ? "dark" : "light";

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolved);
    return () => document.documentElement.removeAttribute("data-theme");
  }, [resolved]);

  const auth = React.use(LoggedInUserContext);
  const signedIn = Boolean(auth && auth.signedIn);

  const {
    filters,
    results,
    meta,
    loading,
    error,
    update,
    setPage,
    reset,
    activeFilterCount,
    perPage,
    commitSearch,
    searchResetToken,
  } = useWebinarsIndex();

  // Memoized: 12 cards, each a subgrid of styled-components. Without this the
  // whole grid is rebuilt on any parent render.
  const body = React.useMemo(() => {
    if (error) {
      return (
        <EmptyState role="alert">
          Terjadi kesalahan saat memuat webinar. Silakan muat ulang halaman.
        </EmptyState>
      );
    }
    if (loading) {
      return (
        <Grid>
          {Array.from({ length: perPage }).map((_, i) =>
            WebinarGridSkeleton(i),
          )}
        </Grid>
      );
    }
    if (results.length === 0) {
      return (
        <EmptyState role="status">
          Tidak ada webinar yang cocok dengan filter Anda.
          {activeFilterCount > 0 && (
            <>
              {" "}
              <InlineReset type="button" onClick={reset}>
                Reset filter
              </InlineReset>
            </>
          )}
        </EmptyState>
      );
    }
    return <Grid>{results.map((w) => WebinarGridCard(w))}</Grid>;
  }, [error, loading, results, perPage, activeFilterCount, reset]);

  return (
    <ProvideLandingSearch>
      <Page data-theme={resolved}>
        <Header />
        <Main>
          <PageHeader>
            <Title>Semua Webinar dan Live Online Classes</Title>
            <Subtitle>
              Jelajahi seluruh webinar &amp; live online class Taxsam. Cari,
              filter, dan temukan rekaman yang bisa Anda tonton.
            </Subtitle>
          </PageHeader>

          <SectionErrorBoundary name="WebinarsFilter">
            <FilterBar
              filters={filters}
              update={update}
              reset={reset}
              activeFilterCount={activeFilterCount}
              signedIn={signedIn}
              themeOptions={themeOptions}
              typeNameOptions={typeNameOptions}
              commitSearch={commitSearch}
              searchResetToken={searchResetToken}
              loading={loading}
            />
          </SectionErrorBoundary>

          <ResultMeta aria-live="polite">
            {loading
              ? "Memuat webinar…"
              : error
                ? "Gagal memuat webinar."
                : `Menampilkan ${results.length} dari ${meta.total} webinar`}
          </ResultMeta>

          {body}

          {meta.limited &&
            (meta.limit_reason === "subscribe" ? (
              <UnlockPanel
                title="Buka seluruh arsip webinar"
                actions={[
                  { label: "Lihat Paket Berlangganan", href: "/memberships" },
                ]}
              >
                <p>
                  Anda bisa menjelajahi semua webinar yang akan datang tanpa
                  batas. Untuk menelusuri seluruh {meta.total} webinar termasuk
                  arsip yang sudah selesai, aktifkan langganan. Webinar yang
                  sudah Anda ikuti tetap bisa diakses kapan saja lewat filter
                  &quot;Sudah Terdaftar&quot;.
                </p>
              </UnlockPanel>
            ) : (
              <UnlockPanel
                title={`Masuk untuk menjelajahi ${meta.total} webinar`}
                actions={[
                  { label: "Masuk", href: LOGIN_URL },
                  {
                    label: "Daftar Gratis",
                    href: SIGNUP_URL,
                    variant: "secondary",
                  },
                ]}
              >
                <p>
                  Anda sedang melihat halaman pertama. Masuk atau buat akun
                  gratis untuk menelusuri seluruh katalog webinar dan live
                  online class Taxsam.
                </p>
              </UnlockPanel>
            ))}

          <Pager page={meta.page} pages={meta.pages} onChange={setPage} />
        </Main>
        <Footer />
      </Page>
    </ProvideLandingSearch>
  );
}
